import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";

import { type CopilotAttachmentUploadResult, type CopilotDisplayMessage } from "@regal-voice/shared-types";

import { getLogger } from "Services/LoggingService";

import { COPILOT_SSE_CANCEL_ACTION } from "../copilotSseConstants";
import { runCopilotGenerateReplyStream } from "../runCopilotGenerateReplyStream";
import { finalizeStreamMessages } from "./finalizeStreamMessages";

const logger = getLogger("CopilotSessionStreamContext", { regal_copilot: true });

/** Abort SSE if still streaming after this long (background sessions; bounds open connections). */
export const COPILOT_STREAM_MAX_OPEN_MS = 10 * 60 * 1000;

/** If a deferred cancel never sees SSE open (network hang), abort after this long. */
const DEFERRED_CANCEL_MAX_MS = 30_000;

/**
 * Discriminates the source of an in-flight Copilot SSE cancellation. Every
 * code path that calls ``AbortController.abort()`` for a Copilot stream — or
 * that observes an SSE-level failure before the ``done`` event — must tag
 * itself with one of these reasons so Datadog can attribute the cancellation.
 */
export type CopilotCancelReason =
    /** Explicit Stop control in the chat composer. */
    | "user_stop_button"
    /** "New session" button — implicit cancel of any active stream. */
    | "user_new_session"
    /** User submitted a new turn while one was already streaming. */
    | "user_resend"
    /** ``CopilotSessionStreamProvider`` unmount (page teardown, app logout). */
    | "unmount"
    /** ``pagehide`` event — tab close, refresh, hard navigation. */
    | "page_hide"
    /** ``visibilitychange`` to hidden — informational only; no abort fires. */
    | "page_hidden"
    /** Stream open longer than {@link COPILOT_STREAM_MAX_OPEN_MS}. */
    | "max_open_duration"
    /** A deferred user cancel never saw the SSE handshake and timed out. */
    | "deferred_cancel_timeout"
    /** Upstream returned a non-OK response on the initial POST. */
    | "sse_open_failed"
    /** Upstream stream errored before emitting a ``done`` event (likely infra / 5xx). */
    | "sse_error_before_done"
    /** Catch-all for unattributed aborts; should trend to zero in production. */
    | "unknown";

export type CancelCopilotStreamOptions = {
    /**
     * Source of the cancellation; required so the emitted Datadog RUM action
     * (``copilot.sse.cancel``) can attribute the abort. New cancel sites must
     * extend {@link CopilotCancelReason} rather than reusing ``"unknown"``.
     */
    reason: CopilotCancelReason;
    /**
     * When switching sessions, wait until the SSE HTTP handshake completes before aborting so
     * generate-reply is not torn down mid-connection (avoids upstream/client failures).
     */
    deferUntilSseOpen?: boolean;
};

export type CopilotSessionStreamSlice = {
    messages: CopilotDisplayMessage[];
    isStreaming: boolean;
    startedAtMs: number;
};

export type CopilotStreamCompletePayload = {
    finalMessages: CopilotDisplayMessage[];
};

export type StartCopilotSessionStreamParams = {
    sessionId: string;
    userMessageText: string;
    /** Uploaded attachment metadata, shown on the user message as soon as the stream starts. */
    attachmentUploads?: CopilotAttachmentUploadResult[];
    /** Messages already shown for this session before this user turn (e.g. from GET_COPILOT_SESSION). */
    previousMessages: CopilotDisplayMessage[];
    onComplete?: (payload: CopilotStreamCompletePayload) => void | Promise<void>;
    onSessionTitle?: (title: string) => void;
    onWorkflowComplete?: (workflow?: string) => void;
    onError?: (error: Error) => void;
};

type CopilotSessionStreamContextValue = {
    streamState: Record<string, CopilotSessionStreamSlice>;
    startStream: (params: StartCopilotSessionStreamParams) => Promise<void>;
    /** Abort stream for one session, or all sessions if `sessionId` is `undefined`. */
    cancelStream: (sessionId: string | undefined, options: CancelCopilotStreamOptions) => void;
    /** Abort every session stream except `exceptId` (uses latest provider state, not a stale React snapshot). */
    cancelStreamsExcept: (exceptId: string, options: CancelCopilotStreamOptions) => void;
    cancelAllStreams: (options: CancelCopilotStreamOptions) => void;
    hasAnyStreamingSession: boolean;
    isSessionStreaming: (sessionId: string | null | undefined) => boolean;
    /** Stash final messages before `onComplete` so the editor can pick them up after remount. */
    stashPendingFinalMessages: (sessionId: string, messages: CopilotDisplayMessage[]) => void;
    clearPendingFinalMessages: (sessionId: string) => void;
    /** Returns and clears pending final messages for `sessionId`, or `null` if none. */
    takePendingFinalMessages: (sessionId: string) => CopilotDisplayMessage[] | null;
};

const CopilotSessionStreamContext = createContext<CopilotSessionStreamContextValue | null>(null);

export function CopilotSessionStreamProvider({ children }: { children: ReactNode }): JSX.Element {
    const streamRunnerRef = useRef(runCopilotGenerateReplyStream);
    streamRunnerRef.current = runCopilotGenerateReplyStream;

    const [streamState, setStreamState] = useState<Record<string, CopilotSessionStreamSlice>>({});
    const abortControllersRef = useRef(new Map<string, AbortController>());
    const streamStateRef = useRef(streamState);
    streamStateRef.current = streamState;
    /** Updated synchronously inside message `setStreamState` updaters so `onComplete` sees the latest list before re-render. */
    const latestStreamMessagesBySessionRef = useRef<Record<string, CopilotDisplayMessage[]>>({});
    const pendingFinalMessagesBySessionRef = useRef<Record<string, CopilotDisplayMessage[]>>({});
    const maxOpenDurationTimerBySessionRef = useRef(new Map<string, number>());

    const sseOpenedBySessionRef = useRef(new Set<string>());
    const pendingDeferredCancelRef = useRef(new Set<string>());
    const deferredCancelTimerBySessionRef = useRef(new Map<string, number>());

    const clearDeferredCancelTimer = useCallback((sessionId: string) => {
        const timer = deferredCancelTimerBySessionRef.current.get(sessionId);
        if (timer !== undefined) {
            window.clearTimeout(timer);
            deferredCancelTimerBySessionRef.current.delete(sessionId);
        }
    }, []);

    const purgeLifecycleMarkers = useCallback(
        (sessionId: string) => {
            sseOpenedBySessionRef.current.delete(sessionId);
            pendingDeferredCancelRef.current.delete(sessionId);
            clearDeferredCancelTimer(sessionId);
        },
        [clearDeferredCancelTimer]
    );

    const clearMaxOpenDurationTimer = useCallback((sessionId: string) => {
        const timer = maxOpenDurationTimerBySessionRef.current.get(sessionId);
        if (timer !== undefined) {
            window.clearTimeout(timer);
            maxOpenDurationTimerBySessionRef.current.delete(sessionId);
        }
    }, []);

    const stashPendingFinalMessages = useCallback((sessionId: string, messages: CopilotDisplayMessage[]) => {
        pendingFinalMessagesBySessionRef.current[sessionId] = [...messages];
    }, []);

    const clearPendingFinalMessages = useCallback((sessionId: string) => {
        delete pendingFinalMessagesBySessionRef.current[sessionId];
    }, []);

    const takePendingFinalMessages = useCallback((sessionId: string): CopilotDisplayMessage[] | null => {
        const pending = pendingFinalMessagesBySessionRef.current[sessionId];
        if (pending == null) {
            return null;
        }
        delete pendingFinalMessagesBySessionRef.current[sessionId];
        return pending;
    }, []);

    const removeSessionSlice = useCallback(
        (sessionId: string) => {
            clearMaxOpenDurationTimer(sessionId);
            delete latestStreamMessagesBySessionRef.current[sessionId];
            purgeLifecycleMarkers(sessionId);
            abortControllersRef.current.delete(sessionId);
            setStreamState((s) => {
                if (!(sessionId in s)) {
                    return s;
                }
                const rest = { ...s };
                delete rest[sessionId];
                return rest;
            });
        },
        [clearMaxOpenDurationTimer, purgeLifecycleMarkers]
    );

    /**
     * Emits the ``copilot.sse.cancel`` Datadog RUM action with the supplied
     * ``reason`` and stream context. Call this BEFORE the underlying
     * ``AbortController`` is torn down so ``streamingMs`` / ``messageCount``
     * still reflect the in-flight stream.
     */
    const emitCancelAction = useCallback(
        (sessionId: string | undefined, reason: CopilotCancelReason, extra?: Record<string, unknown>) => {
            const slice = sessionId !== undefined ? streamStateRef.current[sessionId] : undefined;
            logger.action(COPILOT_SSE_CANCEL_ACTION, {
                reason,
                sessionId,
                streamingMs: slice ? Date.now() - slice.startedAtMs : undefined,
                messageCount: slice?.messages.length,
                ...extra,
            });
        },
        []
    );

    const abortSessionNow = useCallback(
        (sessionId: string) => {
            abortControllersRef.current.get(sessionId)?.abort();
            removeSessionSlice(sessionId);
        },
        [removeSessionSlice]
    );

    const markSseOpenedForSession = useCallback(
        (sessionId: string) => {
            sseOpenedBySessionRef.current.add(sessionId);
            if (!pendingDeferredCancelRef.current.has(sessionId)) {
                return;
            }
            pendingDeferredCancelRef.current.delete(sessionId);
            clearDeferredCancelTimer(sessionId);
            abortSessionNow(sessionId);
        },
        [abortSessionNow, clearDeferredCancelTimer]
    );

    const scheduleDeferredCancel = useCallback(
        (sessionId: string) => {
            pendingDeferredCancelRef.current.add(sessionId);
            if (deferredCancelTimerBySessionRef.current.has(sessionId)) {
                return;
            }
            const timer = window.setTimeout(() => {
                deferredCancelTimerBySessionRef.current.delete(sessionId);
                if (!pendingDeferredCancelRef.current.has(sessionId)) {
                    return;
                }
                pendingDeferredCancelRef.current.delete(sessionId);
                // The original caller's reason already fired when the deferred
                // cancel was scheduled; tag this distinct event so we can spot
                // streams that never finished their SSE handshake.
                emitCancelAction(sessionId, "deferred_cancel_timeout");
                abortSessionNow(sessionId);
            }, DEFERRED_CANCEL_MAX_MS);
            deferredCancelTimerBySessionRef.current.set(sessionId, timer);
        },
        [abortSessionNow, emitCancelAction]
    );

    const cancelStream = useCallback(
        (sessionId: string | undefined, options: CancelCopilotStreamOptions) => {
            if (sessionId === undefined) {
                for (const sid of Object.keys(streamStateRef.current)) {
                    emitCancelAction(sid, options.reason);
                }
                for (const sid of [...deferredCancelTimerBySessionRef.current.keys()]) {
                    clearDeferredCancelTimer(sid);
                }
                deferredCancelTimerBySessionRef.current.clear();
                pendingDeferredCancelRef.current.clear();
                sseOpenedBySessionRef.current.clear();
                for (const [, ac] of abortControllersRef.current) {
                    ac.abort();
                }
                abortControllersRef.current.clear();
                latestStreamMessagesBySessionRef.current = {};
                pendingFinalMessagesBySessionRef.current = {};
                for (const sid of [...maxOpenDurationTimerBySessionRef.current.keys()]) {
                    clearMaxOpenDurationTimer(sid);
                }
                maxOpenDurationTimerBySessionRef.current.clear();
                setStreamState({});
                return;
            }

            const defer = options.deferUntilSseOpen === true;
            const hasController = abortControllersRef.current.has(sessionId);
            if (defer && hasController && !sseOpenedBySessionRef.current.has(sessionId)) {
                emitCancelAction(sessionId, options.reason, { deferred: true });
                scheduleDeferredCancel(sessionId);
                return;
            }

            if (hasController) {
                emitCancelAction(sessionId, options.reason);
            }
            abortSessionNow(sessionId);
        },
        [abortSessionNow, clearDeferredCancelTimer, clearMaxOpenDurationTimer, emitCancelAction, scheduleDeferredCancel]
    );

    const cancelStreamsExcept = useCallback(
        (exceptId: string, options: CancelCopilotStreamOptions) => {
            for (const id of Object.keys(streamStateRef.current)) {
                if (id !== exceptId) {
                    cancelStream(id, options);
                }
            }
        },
        [cancelStream]
    );

    const cancelAllStreams = useCallback(
        (options: CancelCopilotStreamOptions) => {
            cancelStream(undefined, options);
        },
        [cancelStream]
    );

    useEffect(() => {
        return () => {
            for (const sid of abortControllersRef.current.keys()) {
                emitCancelAction(sid, "unmount");
            }
            for (const sid of [...deferredCancelTimerBySessionRef.current.keys()]) {
                clearDeferredCancelTimer(sid);
            }
            deferredCancelTimerBySessionRef.current.clear();
            pendingDeferredCancelRef.current.clear();
            sseOpenedBySessionRef.current.clear();
            for (const [, ac] of abortControllersRef.current) {
                ac.abort();
            }
            abortControllersRef.current.clear();
            latestStreamMessagesBySessionRef.current = {};
            pendingFinalMessagesBySessionRef.current = {};
            for (const sid of [...maxOpenDurationTimerBySessionRef.current.keys()]) {
                clearMaxOpenDurationTimer(sid);
            }
            maxOpenDurationTimerBySessionRef.current.clear();
        };
    }, [clearDeferredCancelTimer, clearMaxOpenDurationTimer, emitCancelAction]);

    /**
     * Capture browser-initiated disconnects so they show up alongside our
     * explicit cancel reasons in Datadog. ``pagehide`` is the most reliable
     * signal for tab close / refresh / hard navigation across desktop and
     * mobile; ``visibilitychange`` is purely informational because hidden tabs
     * can still resume (we record the action but do not abort).
     */
    useEffect(() => {
        const handlePageHide = () => {
            if (abortControllersRef.current.size === 0) {
                return;
            }
            cancelAllStreams({ reason: "page_hide" });
        };
        const handleVisibilityChange = () => {
            if (typeof document === "undefined" || document.visibilityState !== "hidden") {
                return;
            }
            for (const sid of abortControllersRef.current.keys()) {
                emitCancelAction(sid, "page_hidden");
            }
        };
        window.addEventListener("pagehide", handlePageHide);
        document.addEventListener("visibilitychange", handleVisibilityChange);
        return () => {
            window.removeEventListener("pagehide", handlePageHide);
            document.removeEventListener("visibilitychange", handleVisibilityChange);
        };
    }, [cancelAllStreams, emitCancelAction]);

    const startStream = useCallback(
        async (params: StartCopilotSessionStreamParams) => {
            const { sessionId, userMessageText, previousMessages, attachmentUploads } = params;

            const prevAc = abortControllersRef.current.get(sessionId);
            if (prevAc) {
                emitCancelAction(sessionId, "user_resend");
                purgeLifecycleMarkers(sessionId);
                prevAc.abort();
                abortControllersRef.current.delete(sessionId);
                clearMaxOpenDurationTimer(sessionId);
            }

            const ac = new AbortController();
            abortControllersRef.current.set(sessionId, ac);
            const startedAtMs = Date.now();

            // Functional setState so concurrent ``startStream`` calls for different
            // sessions cannot clobber each other under React 18's automatic batching
            // (the previous ``streamStateRef.current``-based pattern could see a stale
            // snapshot if React had not yet committed an earlier setState).
            setStreamState((prev) => ({
                ...prev,
                [sessionId]: {
                    messages: [...previousMessages],
                    isStreaming: true,
                    startedAtMs,
                },
            }));
            latestStreamMessagesBySessionRef.current[sessionId] = [...previousMessages];

            clearMaxOpenDurationTimer(sessionId);
            const maxOpenTimer = window.setTimeout(() => {
                maxOpenDurationTimerBySessionRef.current.delete(sessionId);
                const slice = streamStateRef.current[sessionId];
                const stillStreaming = Boolean(slice?.isStreaming) && abortControllersRef.current.has(sessionId);
                if (stillStreaming) {
                    logger.log("Copilot stream aborted after max open duration", { sessionId });
                    emitCancelAction(sessionId, "max_open_duration");
                    abortSessionNow(sessionId);
                }
            }, COPILOT_STREAM_MAX_OPEN_MS);
            maxOpenDurationTimerBySessionRef.current.set(sessionId, maxOpenTimer);

            const updateMessages = (updater: (prev: CopilotDisplayMessage[]) => CopilotDisplayMessage[]) => {
                setStreamState((s) => {
                    const cur = s[sessionId];
                    if (!cur) {
                        return s;
                    }
                    const nextMessages = updater(cur.messages);
                    latestStreamMessagesBySessionRef.current[sessionId] = nextMessages;
                    return {
                        ...s,
                        [sessionId]: {
                            ...cur,
                            messages: nextMessages,
                            isStreaming: true,
                        },
                    };
                });
            };

            try {
                await streamRunnerRef.current({
                    signal: ac.signal,
                    sessionId,
                    systemPrompt: "",
                    userMessageText,
                    attachmentUploads,
                    updateMessages,
                    onSseOpened: () => {
                        markSseOpenedForSession(sessionId);
                    },
                    onSessionTitle: params.onSessionTitle,
                    onWorkflowComplete: params.onWorkflowComplete,
                    onError: params.onError,
                    onComplete: async () => {
                        const rawMessages = latestStreamMessagesBySessionRef.current[sessionId] ?? [];
                        const finalMessages = finalizeStreamMessages(rawMessages);
                        stashPendingFinalMessages(sessionId, finalMessages);
                        try {
                            await params.onComplete?.({ finalMessages });
                        } finally {
                            removeSessionSlice(sessionId);
                        }
                    },
                });
            } catch (error) {
                const err = error instanceof Error ? error : new Error(String(error));
                if (err.name !== "AbortError") {
                    logger.error("Copilot session stream failed", { error: err, sessionId });
                }
                removeSessionSlice(sessionId);
            }
        },
        [
            abortSessionNow,
            clearMaxOpenDurationTimer,
            emitCancelAction,
            markSseOpenedForSession,
            purgeLifecycleMarkers,
            removeSessionSlice,
            stashPendingFinalMessages,
        ]
    );

    const hasAnyStreamingSession = useMemo(
        () => Object.values(streamState).some((slice) => slice.isStreaming),
        [streamState]
    );

    const isSessionStreaming = useCallback(
        (sessionId: string | null | undefined) => Boolean(sessionId && streamState[sessionId]?.isStreaming),
        [streamState]
    );

    const value = useMemo(
        () => ({
            streamState,
            startStream,
            cancelStream,
            cancelStreamsExcept,
            cancelAllStreams,
            hasAnyStreamingSession,
            isSessionStreaming,
            stashPendingFinalMessages,
            clearPendingFinalMessages,
            takePendingFinalMessages,
        }),
        [
            streamState,
            startStream,
            cancelStream,
            cancelStreamsExcept,
            cancelAllStreams,
            hasAnyStreamingSession,
            isSessionStreaming,
            stashPendingFinalMessages,
            clearPendingFinalMessages,
            takePendingFinalMessages,
        ]
    );

    return <CopilotSessionStreamContext.Provider value={value}>{children}</CopilotSessionStreamContext.Provider>;
}

export function useCopilotSessionStream(): CopilotSessionStreamContextValue {
    const ctx = useContext(CopilotSessionStreamContext);
    if (!ctx) {
        throw new Error("useCopilotSessionStream must be used within CopilotSessionStreamProvider");
    }
    return ctx;
}
